import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../../../utils/supabaseClient';

/**
 * Hook to manage judging rounds for a collection (organizer view).
 * Loads rounds, judges, and provides CRUD operations.
 */
export function useJudgingRounds(collectionId) {
  const [rounds, setRounds] = useState([]);
  const [judges, setJudges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRounds = useCallback(async () => {
    if (!collectionId) return;
    try {
      setLoading(true);
      setError(null);

      const { data, error: err } = await supabase
        .from('collection_judging_rounds')
        .select('*')
        .eq('collection_id', collectionId)
        .order('created_at', { ascending: false });

      if (err) throw err;
      setRounds(data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [collectionId]);

  const fetchJudges = useCallback(async () => {
    if (!collectionId) return;
    try {
      const { data, error: err } = await supabase
        .from('collection_judges')
        .select(`
          *,
          users:user_uuid (
            uuid, first_name, last_name, nickname, handle, email, profile_img
          )
        `)
        .eq('collection_id', collectionId);

      if (err) throw err;
      setJudges(data || []);
    } catch (e) {
      console.error('Error fetching judges:', e);
    }
  }, [collectionId]);

  useEffect(() => {
    fetchRounds();
    fetchJudges();
  }, [fetchRounds, fetchJudges]);

  const createRound = async (roundData) => {
    const { data, error: err } = await supabase
      .from('collection_judging_rounds')
      .insert({
        collection_id: collectionId,
        ...roundData,
      })
      .select()
      .single();

    if (err) throw err;
    await fetchRounds();
    return data;
  };

  const updateRound = async (roundId, updates) => {
    const { error: err } = await supabase
      .from('collection_judging_rounds')
      .update(updates)
      .eq('id', roundId);

    if (err) throw err;
    await fetchRounds();
  };

  const inviteJudge = async (userUuid, invitedBy) => {
    const { data, error: err } = await supabase
      .from('collection_judges')
      .insert({
        collection_id: collectionId,
        user_uuid: userUuid,
        invited_by: invitedBy,
        status: 'invited',
      })
      .select()
      .single();

    if (err) throw err;
    await fetchJudges();
    return data;
  };

  const updateJudgeStatus = async (judgeId, status) => {
    const { error: err } = await supabase
      .from('collection_judges')
      .update({ status, ...(status === 'active' ? { accepted_at: new Date().toISOString() } : {}) })
      .eq('id', judgeId);

    if (err) throw err;
    await fetchJudges();
  };

  const removeJudge = async (judgeId) => {
    const { error: err } = await supabase
      .from('collection_judges')
      .delete()
      .eq('id', judgeId);

    if (err) throw err;
    await fetchJudges();
  };

  return {
    rounds,
    judges,
    loading,
    error,
    createRound,
    updateRound,
    inviteJudge,
    updateJudgeStatus,
    removeJudge,
    refresh: () => { fetchRounds(); fetchJudges(); },
  };
}

/**
 * Hook for judge scoring UI — calls RPC functions.
 */
export function useJudgeScoring(roundId, judgeUuid) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchArtworks = useCallback(async () => {
    if (!roundId || !judgeUuid) return;
    try {
      setLoading(true);
      setError(null);

      const { data: result, error: err } = await supabase.rpc(
        'get_judging_round_artworks',
        {
          round_id_param: roundId,
          judge_uuid_param: judgeUuid,
        }
      );

      if (err) throw err;
      if (result?.error) {
        setError(result.error);
        return;
      }
      setData(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [roundId, judgeUuid]);

  useEffect(() => {
    fetchArtworks();
  }, [fetchArtworks]);

  const submitScore = async (vcId, score, notes = null) => {
    try {
      setSubmitting(true);
      const { data: result, error: err } = await supabase.rpc(
        'upsert_judge_score',
        {
          round_id_param: roundId,
          judge_uuid_param: judgeUuid,
          vc_id_param: vcId,
          score_param: score,
          notes_param: notes,
        }
      );

      if (err) throw err;
      if (result?.error) throw new Error(result.error);

      // Refresh artworks to update progress and scores
      await fetchArtworks();
      return result;
    } finally {
      setSubmitting(false);
    }
  };

  return { data, loading, error, submitting, submitScore, refresh: fetchArtworks };
}

/**
 * Hook for admin results view — calls get_judging_results RPC.
 */
export function useJudgingResults(roundId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchResults = useCallback(async () => {
    if (!roundId) return;
    try {
      setLoading(true);
      setError(null);

      const { data: result, error: err } = await supabase.rpc(
        'get_judging_results',
        { round_id_param: roundId }
      );

      if (err) throw err;
      if (result?.error) {
        setError(result.error);
        return;
      }
      setData(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [roundId]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  return { data, loading, error, refresh: fetchResults };
}
